/**
 * @prettier
 * @flow
 */
import { useMediaQuery } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { BigNumber } from 'bignumber.js';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { terminalPosEmulatorApi } from 'src/api/terminalPosEmulatorApi';
import type { PushTerminalInstructionRequest } from 'src/api/types/terminalPosApi/PushTerminalInstructionRequest';
import type { TerminalInstructionVm } from 'src/api/types/TerminalInstructionVm';
import { terminalApiUrl } from 'src/api/terminalApiUrl';
import { terminalApiCode } from 'src/api/terminalApiCode';
import { terminalWebhookUrl } from 'src/api/terminalWebhookUrl';
import { terminalPosApi } from 'src/api/terminalPosApi';
import { theme } from 'src/AppRoot';
import { Form } from 'src/components/form/Form';
import { FormCurrencyNumberStringField } from 'src/components/form/FormCurrencyNumberStringField';
import { FormEnvironmentSelect } from 'src/components/form/FormEnvironmentSelect';
import { FormInstructionSelect } from 'src/components/form/FormInstructionSelect';
import { FormMsiSelect } from 'src/components/form/FormMsiSelect';
import { FormTextField } from 'src/components/form/FormTextField';
import { Environments } from 'src/constants/Environment';
import { TerminalInstructionReceivedResults } from 'src/constants/TerminalInstructionReceivedResult';
import type { DeviceSerialNumber, TerminalInstructionId, Url } from 'src/types/Id';
import { alertKnownErrorOrSomethingWentWrong } from 'src/utils/alert/alertKnownErrorOrSomethingWentWrong';
import { parseBoolean } from 'src/utils/boolean/parseBoolean';
import { formatAsCurrency } from 'src/utils/currency/formatAsCurrency';
import { getApphookInLocaleStorage } from 'src/utils/localstorage/getApphookInLocaleStorage';
import { getDeviceInLocaleStorage } from 'src/utils/localstorage/getDeviceInLocaleStorage';
import { setApphookInLocaleStorage } from 'src/utils/localstorage/setApphookInLocaleStorage';
import { setDeviceInLocaleStorage } from 'src/utils/localstorage/setDeviceInLocaleStorage';
import { getEnvironmentInLocaleStorage } from 'src/utils/localstorage/getEnvironmentInLocaleStorage';
import { setEnvironmentInLocaleStorage } from 'src/utils/localstorage/setEnvironmentInLocaleStorage';
import { removeNulls } from 'src/utils/removeNulls';
import { sleep } from 'src/utils/sleep';
import { translate } from 'src/utils/translate/translate';
import type { Environment } from 'src/constants/Environment';

export function PushPosEmulatorPage() {
    const classes = useStyles();

    const [pushedTerminalInstruction, setPushedTerminalInstruction] = useState<PushTerminalInstructionRequest | void>();
    const [terminalPosApiResponse, setTerminalPosApiResponse] = useState<any>();
    const [terminalPosApiResponseWaitingCount, setTerminalPosApiResponseWaitingCount] = useState<number>(0);
    const [webhookTerminalInstruction, setWebhookTerminalInstruction] = useState<TerminalInstructionVm | void>();
    const [webhookTerminalInstructionWaitingCount, setWebhookTerminalInstructionWaitingCount] = useState<number>(0);
    const [receiveOnWebhook, setReceiveOnWebhook] = useState<string | void>(undefined);
    const downSm = useMediaQuery(theme.breakpoints.down('sm'));

    const form = useForm<FormType>();

    const environment = form.watch('environment', getEnvironmentInLocaleStorage());
    const webhook = form.watch('webhook', terminalWebhookUrl(environment));
    const instruction = form.watch('instruction', 'PAYMENT');
    const amount = form.watch('amount', '1.1');

    const isCustomWebhook = ![terminalWebhookUrl(Environments.LOCALHOST), terminalWebhookUrl(Environments.TEST), terminalWebhookUrl(Environments.PROD)].includes(webhook);

    const handleEnvironmentChange = (environment) => {
        setEnvironmentInLocaleStorage(environment);
        form.setValue('serverUrl', terminalApiUrl(environment));
        form.setValue('serverApiCode', terminalApiCode(environment));
        if (!isCustomWebhook) form.setValue('webhook', terminalWebhookUrl(environment));
    };

    const handleSubmit = async (formValues: FormType) => {
        setPushedTerminalInstruction(undefined);
        setTerminalPosApiResponse(undefined);
        setTerminalPosApiResponseWaitingCount(0);
        setWebhookTerminalInstruction(undefined);
        setWebhookTerminalInstructionWaitingCount(0);
        setReceiveOnWebhook(formValues.webhook ?? undefined);

        if (formValues.device) setDeviceInLocaleStorage(formValues.device);
        if (formValues.apphook) setApphookInLocaleStorage(formValues.apphook);
        const pushedTerminalInstruction = {
            device: formValues.device ?? undefined,
            webhook: formValues.webhook ?? undefined,
            apphook: formValues.apphook ?? undefined,
            request: (getTerminalInstructionRequestFromForm(formValues): any),
        };
        setPushedTerminalInstruction(pushedTerminalInstruction);

        const responsePromise = terminalPosApi.pushTerminalInstructionV2({
            environment: formValues.environment,
            serverUrl: formValues.serverUrl,
            ...pushedTerminalInstruction,
        });

        const intervalId = setInterval(() => {setTerminalPosApiResponseWaitingCount(terminalApiWaitingCount => terminalApiWaitingCount + 1); }, 1000);
        const terminalPosApiResponse = await responsePromise;
        clearInterval(intervalId);

        if (!terminalPosApiResponse.ok) {
            alertKnownErrorOrSomethingWentWrong(terminalPosApiResponse);
            return;
        }

        const responseData = (terminalPosApiResponse.data: any);
        const terminalInstructionId = responseData?.terminalInstructionId ?? responseData;
        setTerminalPosApiResponse(responseData);

        if ((responseData.result !== TerminalInstructionReceivedResults.INSTRUCTION_RECEIVED && responseData.result !== TerminalInstructionReceivedResults.UNKNOWN) || isCustomWebhook) {
            return;
        }

        while (true) {
            await sleep(1000);
            setWebhookTerminalInstructionWaitingCount(count => count + 1);
            const webhookResponse = await terminalPosEmulatorApi.getWebhookResponse({
                environment: formValues.environment,
                serverUrl: formValues.serverUrl,
                terminalInstructionId,
            });
            if (!webhookResponse.ok) {
                alertKnownErrorOrSomethingWentWrong(webhookResponse);
                return;
            }
            const webhookResponseTerminalInstruction = webhookResponse.data;
            if (webhookResponseTerminalInstruction?.response?.result) {
                setWebhookTerminalInstruction(webhookResponseTerminalInstruction);
                return;
            }
        }
    };

    function getTerminalInstructionRequestFromForm(form) {
        if (form.instruction === 'PAYMENT') {
            return {
                instruction: 'PAYMENT',
                amount: form.amount,
                tip: form.tip || undefined,
                message: form.message || undefined,
                msi: BigNumber(form.msi || 0).isZero() ? undefined : BigNumber(form.msi).toNumber(),
                reference: form.reference || undefined,
                data: form.data ? JSON.parse(form.data) : undefined,
            };
        }
        if (form.instruction === 'CANCEL_PAYMENT') {
            return {
                instruction: 'CANCEL_PAYMENT',
                numeroTransaccion: form.numeroTransaccion,
            };
        }
        if (form.instruction === 'REFUND_PAYMENT') {
            return {
                instruction: 'REFUND_PAYMENT',
                numeroTransaccion: form.numeroTransaccion,
                amount: form.refundAmount || undefined,
            };
        }
        if (form.instruction === 'ABORT_PAYMENT_PROCESS') {
            return {
                instruction: 'ABORT_PAYMENT_PROCESS',
            };
        }
        if (form.instruction === 'PRINT') {
            return {
                instruction: 'PRINT',
                printInstructions: JSON.parse(form.printInstructions),
            };
        }
        if (form.instruction === 'SET_TERMINAL_SETTINGS') {
            return removeNulls({
                instruction: 'SET_TERMINAL_SETTINGS',
                securityCode: form.securityCode,
                posIntegrationEnabled: parseBoolean(form.posIntegrationEnabled),
                kioskMode: parseBoolean(form.kioskMode),
                autoAcceptEnabled: parseBoolean(form.autoAcceptEnabled),
                tipEnabled: parseBoolean(form.tipEnabled),
                qpsEnabled: parseBoolean(form.qpsEnabled),
                preAuthorizationEnabled: parseBoolean(form.preAuthorizationEnabled),
                monthsWithoutInterestEnabled: parseBoolean(form.monthsWithoutInterestEnabled),
                emailVoucherEnabled: parseBoolean(form.emailVoucherEnabled),
                localTerminalPaymentsEnabled: parseBoolean(form.localTerminalPaymentsEnabled),
                paymentMethodEnabled: parseBoolean(form.paymentMethodEnabled),
            });
        }
    }

    return <Grid container spacing={0} classes={{ root: classes.container }}>
        <Grid item xs={12} md={6}
              container
              direction="column"
              justifyContent="flex-start"
              alignItems="center"
              classes={{ root: classes.leftContainer }}>
            <Form form={form} onSubmit={handleSubmit} className={classes.form}>
                <Grid container
                      direction="column"
                      justifyContent="center"
                      alignItems="center"
                >
                    <Typography variant="h4" align="center" color="textSecondary" className={classes.header}>
                        POS Emulator (API Version 2)
                    </Typography>
                </Grid>
                <Grid container spacing={downSm ? 1 : 2}>
                    <Grid item xs={12}>
                        <a href='/'>Go to old version of the api (version 1)</a>
                    </Grid>
                    <Grid item xs={12}>
                        <FormEnvironmentSelect name="environment" label="Environment" defaultValue={getEnvironmentInLocaleStorage()} required onChange={handleEnvironmentChange} />
                    </Grid>

                    <Grid item xs={12}>
                        <FormTextField name="serverUrl" label="Server API URL" defaultValue={terminalApiUrl(environment)} disabled />
                    </Grid>

                    <Grid item xs={12}>
                        <FormTextField name="serverApiCode" label="Server API Code" defaultValue={'Kc1Ip7llsCounOmnBaU0QXb8mSTZlTgP2yyyyfm3xlo2sAFfXUBulQ=='} disabled />
                    </Grid>

                    <Grid item xs={12}>
                        <FormTextField name="device" label="Serial Number of Terminal Device" defaultValue={getDeviceInLocaleStorage()} required autoComplete="on" />
                    </Grid>

                    <Grid item xs={12}>
                        <FormTextField name="webhook" label="Webhook URL" defaultValue={terminalWebhookUrl(environment)} />
                    </Grid>

                    <Grid item xs={12}>
                        <FormTextField name="apphook" label="Apphook Schema" defaultValue={getApphookInLocaleStorage()} />
                    </Grid>

                    <Grid item xs={12}>
                        <FormInstructionSelect name="instruction" label="Instruction" defaultValue={'PAYMENT'} required />
                    </Grid>

                    {instruction === 'PAYMENT' && <>
                        <Grid item xs={12}>
                            <FormCurrencyNumberStringField name="amount" label={'Amount'} defaultValue="1.1" />
                        </Grid>
                        <Grid item xs={12}>
                            <FormCurrencyNumberStringField name="tip" label={'Tip'} defaultValue="" />
                        </Grid>
                        <Grid item xs={12}>
                            <FormTextField name="message" label={'Message'} defaultValue="Mesa 5" />
                        </Grid>
                        <Grid item xs={12}>
                            <FormMsiSelect name="msi" label={'MSI (Meses Sin Intereses, can me 3, 6, 9, 12, 18)'} defaultValue="" />
                        </Grid>
                        <Grid item xs={12}>
                            <FormTextField name="reference" label={'Reference'} defaultValue="" />
                        </Grid>

                        <Grid item xs={12}>
                            <FormTextField name="data" label={'Custom Business Date'} multiline defaultValue='{ "storeId": 123, "folio": "345", "table": { "id": 364 } }' />
                        </Grid>
                    </>
                    }

                    {instruction === 'CANCEL_PAYMENT' && <>
                        <Grid item xs={12}>
                            <FormTextField name="numeroTransaccion" label={'Numero Transaction'} />
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                variant="outlined"
                                color="secondary"
                                fullWidth
                                disabled={form.formState.isSubmitting || (webhookTerminalInstruction && !webhookTerminalInstruction.response)}
                                style={{ height: 56 }}
                                onClick={() => {
                                    if (!(webhookTerminalInstruction?.response: any)?.transaction?.NumeroTransaccion) return;
                                    form.setValue('numeroTransaccion', (webhookTerminalInstruction?.response: any)?.transaction?.NumeroTransaccion);
                                }}
                            >
                                {translate('⬆️ Load previous payment numeroTransaccion ⬆️')}
                            </Button>
                        </Grid>
                    </>
                    }

                    {instruction === 'REFUND_PAYMENT' && <>
                        <Grid item xs={12}>
                            <FormTextField name="numeroTransaccion" label={'Numero Transaction'} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormCurrencyNumberStringField name="refundAmount" label={'Amount'} defaultValue="" />
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                variant="outlined"
                                color="secondary"
                                fullWidth
                                disabled={form.formState.isSubmitting || (webhookTerminalInstruction && !webhookTerminalInstruction.response)}
                                style={{ height: 56 }}
                                onClick={() => {
                                    if (!(webhookTerminalInstruction?.response: any)?.transaction?.NumeroTransaccion) return;
                                    form.setValue('numeroTransaccion', (webhookTerminalInstruction?.response: any)?.transaction?.NumeroTransaccion);
                                }}
                            >
                                {translate('⬆️ Load previous payment numeroTransaccion ⬆️')}
                            </Button>
                        </Grid>
                    </>
                    }

                    {instruction === 'PRINT' && <>
                        <Grid item xs={12}>
                            <FormTextField name="printInstructions" label={'Print Instructions'} multiline defaultValue={getDefaultPrintValue()} />
                        </Grid>
                    </>
                    }

                    {instruction === 'SET_TERMINAL_SETTINGS' && <>
                        <Grid item xs={12}>
                            <FormTextField name="securityCode" label={'Security Code'} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormTextField name="posIntegrationEnabled" label={'Pos Integration Enabled'} helperText='true or false or leave blank' />
                        </Grid>
                        <Grid item xs={12}>
                            <FormTextField name="kioskMode" label={'Kiosk Mode'} helperText='true or false or leave blank' />
                        </Grid>
                        <Grid item xs={12}>
                            <FormTextField name="autoAcceptEnabled" label={'Auto Accept Enabled'} helperText='true or false or leave blank' />
                        </Grid>
                        <Grid item xs={12}>
                            <FormTextField name="tipEnabled" label={'Tip Enabled'} helperText='true or false or leave blank' />
                        </Grid>
                        <Grid item xs={12}>
                            <FormTextField name="qpsEnabled" label={'Qps Enabled'} helperText='true or false or leave blank' />
                        </Grid>
                        <Grid item xs={12}>
                            <FormTextField name="preAuthorizationEnabled" label={'Pre Authorization Enabled'} helperText='true or false or leave blank' />
                        </Grid>
                        <Grid item xs={12}>
                            <FormTextField name="monthsWithoutInterestEnabled" label={'Months Without Interest Enabled'} helperText='true or false or leave blank' />
                        </Grid>
                        <Grid item xs={12}>
                            <FormTextField name="emailVoucherEnabled" label={'Email Voucher Enabled'} helperText='true or false or leave blank' />
                        </Grid>
                        <Grid item xs={12}>
                            <FormTextField name="localTerminalPaymentsEnabled" label={'Local Terminal Payments Enabled'} helperText='true or false or leave blank' />
                        </Grid>
                        <Grid item xs={12}>
                            <FormTextField name="paymentMethodEnabled" label={'Payment Method Enabled'} helperText='true or false or leave blank' />
                        </Grid>
                    </>
                    }

                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            fullWidth
                            disabled={form.formState.isSubmitting}
                            style={{ height: 56 }}
                        >
                            {(form.formState.isSubmitting) &&
                                <CircularProgress color="primary" className={classes.buttonProgress} />}
                            {instruction === 'PAYMENT' ? translate('Pay @amount', { amount: formatAsCurrency(amount) }) : ''}
                            {instruction === 'CANCEL_PAYMENT' ? translate('Cancel Payment') : ''}
                            {instruction === 'REFUND_PAYMENT' ? translate('Refund Payment') : ''}
                            {instruction === 'REFUND_PAYMENT' ? translate('Refund Payment') : ''}
                            {instruction === 'ABORT_PAYMENT_PROCESS' ? translate('Abort Payment Process') : ''}
                            {instruction === 'PRINT' ? translate('Print') : ''}
                            {instruction === 'SET_TERMINAL_SETTINGS' ? translate('Set Terminal Settings') : ''}
                        </Button>
                    </Grid>

                    <Grid item xs={12}>
                        <Box align="center" color="text.disabled">
                            <Typography variant="caption">
                                {translate('This will send a payment instruction to the terminal, wait for the result and show the result to the right.')}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Form>
        </Grid>
        <Grid item xs={12} md={6}
              container
              direction="row"
              justifyContent="center"
              alignItems="flex-start"
              classes={{ root: classes.rightContainer }}
        >

            <div className={classes.rightCenteredContainer}>

                <Grid container
                      direction="column"
                      justifyContent="center"
                      alignItems="center"
                >
                    <Typography variant="h4" align="center" color="textSecondary" className={classes.header}>
                        Result
                    </Typography>
                </Grid>

                <code style={{ textAlign: 'left', margin: 0, width: '100%' }}>
                    {pushedTerminalInstruction &&
                        <div style={{ paddingTop: 8, paddingBottom: 8 }}>
                            <div style={{ whiteSpace: 'nowrap' }}>TERMINAL API REQUEST:</div>
                            <div><pre style={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'keep-all',
                            }}>{JSON.stringify(pushedTerminalInstruction, null, 2)}</pre>
                            </div>

                        </div>
                    }

                    {pushedTerminalInstruction &&
                        <div style={{ paddingTop: 8, paddingBottom: 8 }}>
                            <div style={{ whiteSpace: 'nowrap' }}>TERMINAL API RESPONSE:</div>
                            <div><pre style={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'keep-all',
                            }}>{terminalPosApiResponse ? JSON.stringify(terminalPosApiResponse, null, 2) : `${'.'.repeat(terminalPosApiResponseWaitingCount).replace(/(.{10})/g, '$1\n')}`}</pre>
                            </div>
                        </div>
                    }

                    {terminalPosApiResponse &&
                        <div style={{ paddingTop: 8, paddingBottom: 8 }}>
                            <div style={{ whiteSpace: 'nowrap' }}>WEBHOOK:</div>
                            {isCustomWebhook ?
                                <div><pre style={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'keep-all',
                                }}>Custom webhook provided.<br/>Check your server received webhook on<br/>{receiveOnWebhook}</pre>
                                </div>
                                :
                                <div><pre style={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'keep-all',
                                }}>{webhookTerminalInstruction?.response ? JSON.stringify(webhookTerminalInstruction, null, 2) : `${'.'.repeat(webhookTerminalInstructionWaitingCount).replace(/(.{10})/g, '$1\n')}`}</pre>
                                </div>

                            }
                        </div>
                    }
                </code>

            </div>
        </Grid>
    </Grid>;
}

function getDefaultPrintValue() {
    return `[
    { "instructionType": "TEXT", "text": "Texts", "style": { "align": "CENTER" } },
    { "instructionType": "TEXT", "text": "Left", "style": { "align": "LEFT" } },
    { "instructionType": "TEXT", "text": "Center", "style": { "align": "CENTER" } },
    { "instructionType": "TEXT", "text": "Right", "style": { "align": "RIGHT" } },
    { "instructionType": "TEXT", "text": "Bold", "style": { "align": "CENTER", "fontWeight": "BOLD" } },
    { "instructionType": "TEXT", "text": "Large", "style": { "align": "CENTER", "fontSize": "LARGE" } },
    { "instructionType": "TEXT", "text": "Small", "style": { "align": "CENTER", "fontSize": "SMALL" } },
    { "instructionType": "FEED_LINES", "lines": 3 },
    { "instructionType": "TEXT", "text": "Table Rows", "style": { "align": "CENTER" } },
    { "instructionType": "TABLE_ROW", "left": "Left", "center": "Center", "right": "Right", "style": { "fontWeight": "NORMAL" } }, 
    { "instructionType": "TABLE_ROW", "left": "Bold", "center": "Bold", "right": "Bold", "style": { "fontWeight": "BOLD" } }, 
    { "instructionType": "TABLE_ROW", "left": "Large", "center": "Large", "right": "Large", "style": { "fontSize": "LARGE" } }, 
    { "instructionType": "TABLE_ROW", "left": "Small", "center": "Small", "right": "Small", "style": { "fontSize": "SMALL" } }, 
    { "instructionType": "FEED_LINES", "lines": 3 },
    { "instructionType": "TEXT", "text": "Bitmap Image", "style": { "align": "CENTER" } },
    { "instructionType": "BITMAP", "base64": "iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAAFUlEQVR42mP8z8BQz0AEYBxVSF+FABJADveWkH6oAAAAAElFTkSuQmCC", "align": "CENTER" }, 
    { "instructionType": "FEED_LINES", "lines": 3 },
    { "instructionType": "TEXT", "text": "QR Code", "style": { "align": "CENTER" } },
    { "instructionType": "QR_CODE", "qrCode": "https://dev.azure.com/openlinea/olipayterminal-releases", "align": "CENTER" }, 
    { "instructionType": "FEED_LINES", "lines": 3 },
    { "instructionType": "TEXT", "text": "Barcode", "style": { "align": "CENTER" } },
    { "instructionType": "BAR_CODE", "barCode": "123456789", "align": "CENTER" }, 
    { "instructionType": "FEED_LINES", "lines": 1 }
]`;
}

const useStyles = makeStyles((theme) => ({
    container: {
        height: 'auto',
        [theme.breakpoints.up('md')]: {
            height: '100%',
        },
    },
    leftContainer: {
        padding: theme.spacing(2),
        backgroundColor: 'white',
        borderWidth: 0,
        borderBottomWidth: 1,
        borderColor: '#eee',
        borderStyle: 'solid',
        [theme.breakpoints.up('md')]: {
            padding: theme.spacing(4),
            borderRightWidth: 1,
        },
    },
    rightContainer: {
        padding: theme.spacing(2),
        backgroundColor: '#fafafa',
        [theme.breakpoints.up('md')]: {
            padding: theme.spacing(4),
        },
    },
    rightCenteredContainer: {
        [theme.breakpoints.up('md')]: {
            minHeight: 403,
        },
    },
    header: {
        paddingTop: theme.spacing(0),
        paddingBottom: theme.spacing(4),
        [theme.breakpoints.up('md')]: {
            paddingTop: theme.spacing(0),
        },
    },
    companyName: {
        paddingTop: theme.spacing(2),
        [theme.breakpoints.up('md')]: {
            // paddingTop: theme.spacing(20),
        },
    },
    total: {
        paddingTop: theme.spacing(2),
        [theme.breakpoints.up('md')]: {
            // paddingTop: theme.spacing(12),
        },
    },
    helloText: {
        paddingTop: theme.spacing(6),
        [theme.breakpoints.up('md')]: {
            // paddingTop: theme.spacing(9),
        },
    },
    reasonText: {
        paddingTop: theme.spacing(2),
        [theme.breakpoints.up('md')]: {
            // paddingTop: theme.spacing(9),
        },
    },
    linksContainer: {
        paddingTop: theme.spacing(6),
        [theme.breakpoints.up('md')]: {
            // paddingTop: theme.spacing(22),
        },
        align: 'center',
    },
    link: {
        color: theme.palette.text.disabled,
        textDecoration: 'none',
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        cursor: 'pointer',
    },
    form: {
        maxWidth: 500,
    },
    buttonProgress: {
        position: 'absolute',
    },
}));

type FormType = {|
    serverUrl: Url,
    environment: Environment,
    device: DeviceSerialNumber,
    webhook: Url,
    apphook: string,
    instruction: 'PAYMENT' | 'CANCEL_PAYMENT' | 'REFUND_PAYMENT' | 'ABORT_PAYMENT_PROCESS' | 'PRINT' | 'SET_TERMINAL_SETTINGS',
    amount: string,
    tip: string,
    refundAmount: string,
    message: string,
    msi: string,
    reference: string,
    data: string,
    numeroTransaccion: string,
    printInstructions: string,
    securityCode: string, // TODO: change to boolean in form
    posIntegrationEnabled: string, // TODO: change to boolean in form
    kioskMode: string, // TODO: change to boolean in form
    autoAcceptEnabled: string, // TODO: change to boolean in form
    tipEnabled: string, // TODO: change to boolean in form
    qpsEnabled: string, // TODO: change to boolean in form
    preAuthorizationEnabled: string, // TODO: change to boolean in form
    monthsWithoutInterestEnabled: string, // TODO: change to boolean in form
    emailVoucherEnabled: string, // TODO: change to boolean in form
    localTerminalPaymentsEnabled: string, // TODO: change to boolean in form
    paymentMethodEnabled: string, // TODO: change to boolean in form
|};
